import { useEffect, useMemo, useRef, useState } from "react";
import {
  X, Download, Link as LinkIcon, Lock, Unlock, Ruler, Bookmark, Save, Trash2,
  Check, ChevronsRight,
} from "lucide-react";

import { api, tokenStore, explainError } from "../api/client";
import { fetchSuggestion, saveSuggestion, type DownloadSuggestion } from "../api/issues";

export interface DownloadDialogProps {
  open: boolean;
  onClose(): void;

  /** Source artifact dimensions, for default values + aspect ratio. */
  origW: number;
  origH: number;

  /** "page" = single page (.jpg/.png/.webp via /resize)
   *  "issue" = whole-issue zip of pages
   *  "pdf"   = whole-issue PDF (img2pdf bundling) */
  mode: "page" | "issue" | "pdf";

  /** When mode==="page", the artifact id to resize/serve. */
  artifactId?: number;

  /** When mode==="issue" | "pdf", the issue id whose bundle we'll request. */
  issueId?: number;

  /** Used in the suggested download filename. */
  suggestedBase: string;

  /** Channel + edition context — used to load/save the suggested settings. */
  channelId?: number;
  editionId?: number;
  channelName?: string;
  editionName?: string;
}

type Fmt = "jpeg" | "png" | "webp";

/** Pixel-dimension presets — width-driven, aspect kept by the resize endpoint. */
const SIZE_PRESETS: Array<{ label: string; w: number; sub: string }> = [
  { label: "Web 1024",   w: 1024, sub: "screen" },
  { label: "Print 1600", w: 1600, sub: "tablet" },
  { label: "Hi-res 2400", w: 2400, sub: "print" },
];

/** DPI presets — metadata only, doesn't change pixel count. 350 is the
 *  newspaper-clip standard the existing Print-Newspaper pipeline uses. */
const DPI_PRESETS: Array<{ label: string; dpi: number; sub: string }> = [
  { label: "72",  dpi: 72,  sub: "screen" },
  { label: "150", dpi: 150, sub: "draft" },
  { label: "200", dpi: 200, sub: "office" },
  { label: "300", dpi: 300, sub: "print" },
  { label: "350", dpi: 350, sub: "newspaper" },
];

function withToken(url: string): string {
  const at = tokenStore.access;
  if (!at) return url;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}t=${encodeURIComponent(at)}`;
}

export function DownloadDialog({
  open, onClose, origW, origH, mode, artifactId, issueId, suggestedBase,
  channelId, editionId, channelName, editionName,
}: DownloadDialogProps) {
  const [fmt, setFmt] = useState<Fmt>("jpeg");
  const [width, setWidth] = useState<number>(origW);
  const [height, setHeight] = useState<number>(origH);
  const [quality, setQuality] = useState<number>(100);
  const [dpi, setDpi] = useState<number>(350);
  const [linked, setLinked] = useState<boolean>(true);

  // Track whether the user touched DPI / quality. If untouched AND dims are
  // also unchanged, we omit those params entirely so the backend short-circuit
  // returns the original bytes verbatim (no JPEG re-encode = no quality loss).
  const [dpiTouched, setDpiTouched] = useState(false);
  const [qualityTouched, setQualityTouched] = useState(false);

  // Download progress — covers the dialog with a blocking spinner while the
  // backend assembles the bytes (zip/PDF can take seconds for large issues).
  const [busy, setBusy] = useState(false);
  const [bytesReceived, setBytesReceived] = useState<number>(0);
  const [bytesTotal, setBytesTotal] = useState<number>(0);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // ── Suggested-settings box state ─────────────────────────────────
  const [suggestion, setSuggestion] = useState<DownloadSuggestion | null>(null);
  const [sugW, setSugW] = useState<string>("");      // strings — empty = unset
  const [sugH, setSugH] = useState<string>("");
  const [sugDpi, setSugDpi] = useState<string>("");
  const [sugScope, setSugScope] = useState<"edition" | "publisher">("publisher");
  const [sugSaving, setSugSaving] = useState(false);
  const [sugStatus, setSugStatus] = useState<string | null>(null);

  // Reset when dialog opens with new dimensions
  useEffect(() => {
    if (!open) return;
    setWidth(origW);
    setHeight(origH);
    setQuality(100);          // default to maximum quality
    setDpi(350);              // newspaper print standard
    setFmt("jpeg");
    setLinked(true);
    setDpiTouched(false);     // user hasn't picked a DPI → don't force a re-encode
    setQualityTouched(false);
    setSugStatus(null);
    setBusy(false);
    setBytesReceived(0);
    setBytesTotal(0);
    setDownloadError(null);

    // Load suggestion from backend if we have a channel context
    if (!channelId) {
      setSuggestion(null);
      setSugW(""); setSugH(""); setSugDpi("");
      setSugScope("publisher");
      return;
    }
    void (async () => {
      try {
        const s = await fetchSuggestion(channelId, editionId);
        setSuggestion(s);
        setSugW(s.suggested_width  ? String(s.suggested_width)  : "");
        setSugH(s.suggested_height ? String(s.suggested_height) : "");
        setSugDpi(s.suggested_dpi  ? String(s.suggested_dpi)    : "");
        // default scope: edition if we have an editionId, else publisher
        setSugScope(editionId ? "edition" : "publisher");
        // If a saved suggestion exists, pre-fill the main form too.
        if (s.suggested_width)  setWidth(s.suggested_width);
        if (s.suggested_height) setHeight(s.suggested_height);
        if (s.suggested_dpi)  { setDpi(s.suggested_dpi); setDpiTouched(true); }
      } catch {
        setSuggestion(null);
      }
    })();
  }, [open, origW, origH, channelId, editionId]);

  const aspect = origW && origH ? origW / origH : 1;

  function onWidthChange(v: number) {
    setWidth(v);
    if (linked && aspect) setHeight(Math.round(v / aspect));
  }
  function onHeightChange(v: number) {
    setHeight(v);
    if (linked && aspect) setWidth(Math.round(v * aspect));
  }

  /* Physical print size at the current DPI + pixel size. */
  const printInches = useMemo(() => {
    if (!dpi || dpi <= 0) return null;
    const wIn = (width / dpi).toFixed(2);
    const hIn = (height / dpi).toFixed(2);
    const wCm = (width / dpi * 2.54).toFixed(1);
    const hCm = (height / dpi * 2.54).toFixed(1);
    return { wIn, hIn, wCm, hCm };
  }, [width, height, dpi]);

  const url = useMemo(() => {
    const params = new URLSearchParams();
    const sizeChanged = width !== origW || height !== origH;
    if (sizeChanged) {
      params.set("w", String(width));
      params.set("h", String(height));
    }
    if (fmt !== "jpeg") params.set("fmt", fmt);
    // Only forward quality / dpi if the user actually picked a value.
    // Sending dpi forces the backend to re-encode (PIL save); not sending
    // lets it return the original publisher bytes unchanged.
    if (fmt !== "png" && qualityTouched) params.set("q", String(quality));
    if (dpiTouched) params.set("dpi", String(dpi));
    const qs = params.toString();
    if (mode === "page" && artifactId) {
      return `/api/image/${artifactId}/resize${qs ? `?${qs}` : ""}`;
    }
    if (mode === "issue" && issueId) {
      return `/api/image/issue/${issueId}/download.zip${qs ? `?${qs}` : ""}`;
    }
    if (mode === "pdf" && issueId) {
      return `/api/image/issue/${issueId}/download.pdf${qs ? `?${qs}` : ""}`;
    }
    return "";
  }, [width, height, fmt, quality, dpi, dpiTouched, qualityTouched, mode, artifactId, issueId, origW, origH]);

  const filename = useMemo(() => {
    const ext =
      mode === "issue" ? "zip"
      : mode === "pdf" ? "pdf"
      : (fmt === "jpeg" ? "jpg" : fmt);
    const dim = (width === origW && height === origH) ? "" : `_${width}x${height}`;
    // Only mention DPI in the filename if the user actually picked one —
    // otherwise the file is the publisher's original DPI and naming it
    // "_350dpi" misrepresents what's inside.
    const d = dpiTouched ? `_${dpi}dpi` : "";
    return `${suggestedBase}${dim}${d}.${ext}`;
  }, [mode, fmt, width, height, origW, origH, suggestedBase, dpi, dpiTouched]);

  // Per-page size estimate (very rough — purely UX)
  const sizeEstimate = useMemo(() => {
    const pixels = width * height;
    if (fmt === "png") return `${(pixels * 1.6 / 1024 / 1024).toFixed(1)} MB`;
    const bpp = quality >= 95 ? 0.55 : quality >= 85 ? 0.35 : 0.22;
    return `${(pixels * bpp / 8 / 1024 / 1024).toFixed(2)} MB`;
  }, [width, height, fmt, quality]);

  function copyUrl() {
    void navigator.clipboard.writeText(window.location.origin + withToken(url));
  }

  /* Stream the bytes via axios (so we can hook download progress + show a
     blocking overlay), then save to disk via an object URL. For PDF/zip
     bulk exports the backend can take seconds — this gives the operator
     a real "we're working on it" signal instead of dead time. */
  async function triggerDownload() {
    if (!url || busy) return;
    setBusy(true);
    setBytesReceived(0);
    setBytesTotal(0);
    setDownloadError(null);
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      const r = await api.get(url, {
        responseType: "blob",
        // Bulk re-encodes (e.g. a 24-page issue at custom DPI) can run well
        // past the client's global 30s timeout; the Cancel button is the
        // operator's escape hatch here, not a deadline.
        timeout: 0,
        signal: ctrl.signal,
        onDownloadProgress: (e) => {
          if (typeof e.loaded === "number") setBytesReceived(e.loaded);
          if (typeof e.total === "number" && e.total > 0) setBytesTotal(e.total);
        },
      });
      // Pop the file via a one-shot anchor; the URL revoke covers cleanup.
      const blobUrl = URL.createObjectURL(r.data as Blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 4000);
      onClose();
    } catch (e: unknown) {
      const isAbort = (e as { name?: string } | null)?.name === "CanceledError"
                   || (e as { name?: string } | null)?.name === "AbortError";
      if (!isAbort) setDownloadError(explainError(e));
    } finally {
      setBusy(false);
      abortRef.current = null;
    }
  }

  function cancelDownload() {
    abortRef.current?.abort();
  }

  /* ── Suggested-settings actions ─────────────────────────────────── */
  function parseOpt(s: string): number | null {
    if (!s.trim()) return null;
    const n = Number(s);
    return Number.isFinite(n) && n > 0 ? Math.round(n) : null;
  }

  async function saveSuggested() {
    if (!channelId) return;
    setSugStatus(null); setSugSaving(true);
    try {
      const saved = await saveSuggestion(channelId, {
        edition_id:       sugScope === "edition" ? (editionId ?? null) : null,
        suggested_width:  parseOpt(sugW),
        suggested_height: parseOpt(sugH),
        suggested_dpi:    parseOpt(sugDpi),
      });
      setSuggestion(saved);
      setSugStatus(saved.scope === "none"
        ? "Cleared — no suggestion stored for this scope."
        : `Saved at ${saved.scope} scope.`);
    } catch (e) {
      setSugStatus(explainError(e));
    } finally {
      setSugSaving(false);
    }
  }

  function applySuggestionToForm() {
    const w = parseOpt(sugW), h = parseOpt(sugH), d = parseOpt(sugDpi);
    if (w) setWidth(w);
    if (h) setHeight(h);
    if (!w && h && linked && aspect) setWidth(Math.round(h * aspect));
    if (w && !h && linked && aspect) setHeight(Math.round(w / aspect));
    if (d) { setDpi(d); setDpiTouched(true); }
  }

  async function clearSuggested() {
    setSugW(""); setSugH(""); setSugDpi("");
    await saveSuggested();
  }

  if (!open) return null;

  // While busy: backdrop click and ESC are no-ops so the user can't lose
  // their in-flight download by accident.
  const safeClose = busy ? () => {} : onClose;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
         role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[1px]" onClick={safeClose} />

      <div className="relative np-card w-full max-w-[680px] max-h-[90vh] overflow-auto shadow-plate animate-fade-up">
        {/* header */}
        <div className="px-5 py-3 border-b flex items-center justify-between"
             style={{ borderColor: "var(--rule)" }}>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-folio text-[var(--fg-faint)]">
              {mode === "issue" ? "Bulk export"
                : mode === "pdf" ? "PDF export"
                : "Customise download"}
            </div>
            <h2 className="font-display text-[20px] leading-tight">
              {mode === "issue" ? "Download all pages"
                : mode === "pdf" ? "Build PDF"
                : "Modify download"}
            </h2>
          </div>
          <button onClick={safeClose} disabled={busy} aria-label="Close" className="np-btn-ghost p-2">
            <X size={14} />
          </button>
        </div>

        <div className="p-5 flex flex-col gap-5">
          {/* Format */}
          <div>
            <div className="np-label mb-2">
              {mode === "pdf" ? "Embed format (inside PDF)" : "Format"}
            </div>
            <div className="flex gap-2 flex-wrap items-center">
              {/* PDF can only carry JPEG or PNG — img2pdf doesn't accept WebP. */}
              {(mode === "pdf" ? (["jpeg", "png"] as Fmt[]) : (["jpeg", "png", "webp"] as Fmt[])).map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setFmt(f)}
                  className="np-btn-ghost px-4 py-2"
                  style={fmt === f ? { borderColor: "var(--accent)", color: "var(--fg)" } : undefined}
                >
                  {f.toUpperCase()}
                </button>
              ))}
              <div className="flex-1" />
              <div className="font-mono text-[10px] uppercase tracking-folio text-[var(--fg-faint)]">
                Source: {origW}×{origH}px
              </div>
            </div>
          </div>

          {/* Pixel dimensions */}
          <div>
            <div className="flex items-baseline justify-between mb-2">
              <div className="np-label">Pixel dimensions</div>
              <div className="flex gap-1.5">
                {SIZE_PRESETS.map((p) => (
                  <button
                    key={p.label}
                    type="button"
                    onClick={() => onWidthChange(p.w)}
                    className="np-btn-ghost text-[10px] px-2 py-1"
                    title={p.sub}
                  >
                    {p.label}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => { setWidth(origW); setHeight(origH); }}
                  className="np-btn-ghost text-[10px] px-2 py-1"
                  title="Source pixels"
                >
                  Original
                </button>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="flex-1">
                <label className="np-label">Width (px)</label>
                <input
                  type="number"
                  min={4}
                  max={12000}
                  value={width}
                  onChange={(e) => onWidthChange(Number(e.target.value) || 0)}
                  className="np-input font-mono text-[15px]"
                />
              </div>
              <button
                type="button"
                onClick={() => setLinked(l => !l)}
                aria-label={linked ? "Unlink aspect ratio" : "Link aspect ratio"}
                className="np-btn-ghost p-2 mt-5"
                title={linked ? "Aspect locked — width & height move together" : "Aspect unlocked"}
              >
                {linked ? <Lock size={13} /> : <Unlock size={13} />}
              </button>
              <div className="flex-1">
                <label className="np-label">Height (px)</label>
                <input
                  type="number"
                  min={4}
                  max={12000}
                  value={height}
                  onChange={(e) => onHeightChange(Number(e.target.value) || 0)}
                  className="np-input font-mono text-[15px]"
                />
              </div>
            </div>
            <div className="mt-1 font-mono text-[10px] text-[var(--fg-faint)]">
              Resampling: LANCZOS (highest quality). Aspect {linked ? "locked" : "free"}.
            </div>
          </div>

          {/* DPI */}
          <div>
            <div className="flex items-baseline justify-between mb-2">
              <div className="np-label flex items-center gap-1.5">
                <Ruler size={11} className="text-[var(--fg-muted)]" />
                Print DPI
              </div>
              <div className="flex gap-1.5">
                {DPI_PRESETS.map((p) => (
                  <button
                    key={p.dpi}
                    type="button"
                    onClick={() => { setDpi(p.dpi); setDpiTouched(true); }}
                    className="np-btn-ghost text-[10px] px-2 py-1"
                    style={dpiTouched && dpi === p.dpi ? { borderColor: "var(--accent)", color: "var(--fg)" } : undefined}
                    title={p.sub}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="flex-1 max-w-[180px]">
                <label className="np-label">DPI</label>
                <input
                  type="number"
                  min={36}
                  max={1200}
                  step={1}
                  value={dpi}
                  onChange={(e) => { setDpi(Math.max(36, Math.min(1200, Number(e.target.value) || 36))); setDpiTouched(true); }}
                  className="np-input font-mono text-[15px]"
                />
              </div>

              {printInches && (
                <div className="flex-1 pt-3">
                  <div className="np-label">Prints at</div>
                  <div className="font-mono text-[13px] mt-1">
                    <span className="text-[var(--fg)]">
                      {printInches.wIn} × {printInches.hIn}″
                    </span>
                    <span className="text-[var(--fg-faint)] ml-2">
                      ({printInches.wCm} × {printInches.hCm} cm)
                    </span>
                  </div>
                </div>
              )}
            </div>
            <div className="mt-1 font-mono text-[10px] text-[var(--fg-faint)]">
              DPI tags the file header so print software knows the intended physical size.
              It does <em>not</em> change pixel count — control pixels via Width/Height above.
            </div>
          </div>

          {/* Quality (not relevant for PNG) */}
          {fmt !== "png" && (
            <div>
              <div className="flex justify-between items-baseline">
                <div className="np-label">Quality</div>
                <div className="font-mono text-[12px] tabular-nums">
                  {quality}<span className="text-[var(--fg-faint)]"> / 100</span>
                  {quality === 100 && (
                    <span className="ml-2 text-[var(--accent)] uppercase tracking-folio text-[10px]">max</span>
                  )}
                </div>
              </div>
              <input
                type="range"
                min={50}
                max={100}
                value={quality}
                onChange={(e) => { setQuality(Number(e.target.value)); setQualityTouched(true); }}
                className="w-full mt-1 accent-[var(--accent)]"
              />
              <div className="flex justify-between font-mono text-[10px] text-[var(--fg-faint)] mt-0.5">
                <span>smaller file</span>
                <span>q=95+ uses 4:4:4 chroma (no colour subsampling)</span>
                <span>maximum fidelity</span>
              </div>
            </div>
          )}

          {/* ─── Suggested Settings ─────────────────────────────────────
             Per-publisher / per-edition defaults stored in DB. NOT mandatory
             — fields can be left blank. Later: the dialog auto-prefills the
             main form from these on open, per publisher / edition. */}
          {channelId && (
            <div className="np-card-inset p-4 flex flex-col gap-3"
                 style={{ borderColor: "var(--rule)" }}>
              <div className="flex items-baseline justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <Bookmark size={13} className="text-[var(--accent)]" />
                  <span className="np-label">Suggested settings</span>
                  {suggestion && suggestion.scope !== "none" && (
                    <span className="np-chip np-chip--admin">
                      <span className="np-chip-dot" />
                      saved · {suggestion.scope}
                    </span>
                  )}
                </div>
                <div className="font-mono text-[10px] text-[var(--fg-faint)]">
                  Optional. Stored for this {sugScope === "edition" ? "edition" : "publisher"}.
                </div>
              </div>

              <p className="font-sans text-[12px] text-[var(--fg-muted)] leading-snug -mt-1">
                Save the values you typically want for{" "}
                <span className="font-display italic">{channelName ?? "this publisher"}</span>
                {sugScope === "edition" && editionName && <> · <span className="font-display italic">{editionName}</span></>}
                . All three fields are optional — blanks fall through to the
                hard defaults (q=100, dpi=350, source-pixel size).
              </p>

              {/* scope toggle */}
              {editionId && (
                <div className="flex items-center gap-1.5 self-start">
                  <button
                    type="button"
                    onClick={() => setSugScope("edition")}
                    className="np-btn-ghost px-3 py-1 text-[10px]"
                    style={sugScope === "edition"
                      ? { borderColor: "var(--accent)", color: "var(--fg)" }
                      : undefined}
                  >
                    This edition only
                  </button>
                  <button
                    type="button"
                    onClick={() => setSugScope("publisher")}
                    className="np-btn-ghost px-3 py-1 text-[10px]"
                    style={sugScope === "publisher"
                      ? { borderColor: "var(--accent)", color: "var(--fg)" }
                      : undefined}
                  >
                    All editions of publisher
                  </button>
                </div>
              )}

              {/* 3 optional inputs */}
              <div className="grid grid-cols-3 gap-3">
                <label className="flex flex-col">
                  <span className="np-label">Width X (px)</span>
                  <input
                    type="number"
                    min={4} max={12000} placeholder="—"
                    value={sugW}
                    onChange={(e) => setSugW(e.target.value)}
                    className="np-input font-mono text-[14px]"
                  />
                </label>
                <label className="flex flex-col">
                  <span className="np-label">Height Y (px)</span>
                  <input
                    type="number"
                    min={4} max={12000} placeholder="—"
                    value={sugH}
                    onChange={(e) => setSugH(e.target.value)}
                    className="np-input font-mono text-[14px]"
                  />
                </label>
                <label className="flex flex-col">
                  <span className="np-label">DPI</span>
                  <input
                    type="number"
                    min={36} max={1200} placeholder="—"
                    value={sugDpi}
                    onChange={(e) => setSugDpi(e.target.value)}
                    className="np-input font-mono text-[14px]"
                  />
                </label>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={applySuggestionToForm}
                  className="np-btn-ghost text-[11px]"
                  title="Copy these values into the main form above"
                >
                  <ChevronsRight size={11} /> Apply to download form
                </button>
                <div className="flex-1" />
                {suggestion?.scope && suggestion.scope !== "none" && (
                  <button
                    type="button"
                    onClick={clearSuggested}
                    disabled={sugSaving}
                    className="np-btn-ghost text-[11px]"
                    title="Delete the saved suggestion"
                  >
                    <Trash2 size={11} /> Clear
                  </button>
                )}
                <button
                  type="button"
                  onClick={saveSuggested}
                  disabled={sugSaving}
                  className="np-btn text-[11px] px-4 py-2"
                >
                  {sugSaving
                    ? <>Saving…</>
                    : <><Save size={11} /> Save suggestion</>
                  }
                </button>
              </div>

              {sugStatus && (
                <div className="font-mono text-[11px] text-[var(--fg-muted)] flex items-center gap-1.5">
                  <Check size={11} /> {sugStatus}
                </div>
              )}
            </div>
          )}

          {/* Summary */}
          <div className="np-card-inset px-4 py-3 flex items-center justify-between">
            <div className="min-w-0">
              <div className="font-mono text-[10px] uppercase tracking-folio text-[var(--fg-faint)]">
                Output
              </div>
              <div className="font-mono text-[13px] truncate max-w-[420px]">
                {filename}
              </div>
            </div>
            <div className="text-right">
              <div className="font-mono text-[10px] uppercase tracking-folio text-[var(--fg-faint)]">
                {mode === "issue" ? "Per page" : "Estimated size"}
              </div>
              <div className="font-display text-[18px] tabular-nums">
                {sizeEstimate}
              </div>
            </div>
          </div>

          {/* Last-action error (if a previous download attempt failed) */}
          {downloadError && (
            <div className="np-banner" role="alert">
              <span className="font-mono text-[10px] uppercase tracking-folio mr-2 text-[var(--accent)]">
                Download failed
              </span>
              {downloadError}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-2 justify-end">
            <button onClick={copyUrl} disabled={busy} className="np-btn-ghost" title="Copy URL with auth token">
              <LinkIcon size={12} /> Copy link
            </button>
            <button
              type="button"
              onClick={() => void triggerDownload()}
              disabled={busy || !url}
              className="np-btn"
            >
              <Download size={13} />
              {mode === "issue" ? "Download zip"
                : mode === "pdf" ? "Build PDF"
                : "Download"}
            </button>
          </div>
        </div>

        {/* Blocking overlay while we're fetching the bytes — keeps the user
           informed that the server is assembling the file (can take several
           seconds for PDF / large zips). Cancel aborts the in-flight axios. */}
        {busy && <BuildingOverlay
                   mode={mode}
                   bytesReceived={bytesReceived}
                   bytesTotal={bytesTotal}
                   onCancel={cancelDownload}
                 />}
      </div>
    </div>
  );
}

/* ─── Building overlay ──────────────────────────────────────────────── */
function BuildingOverlay({
  mode, bytesReceived, bytesTotal, onCancel,
}: {
  mode: "page" | "issue" | "pdf";
  bytesReceived: number;
  bytesTotal: number;
  onCancel: () => void;
}) {
  const headline = mode === "pdf" ? "Building PDF…"
                 : mode === "issue" ? "Assembling zip…"
                 : "Preparing image…";
  const sub = mode === "pdf"
      ? "Embedding pages into a single PDF on the server."
      : mode === "issue"
      ? "Each page is being prepared on the server."
      : "Re-encoding at your chosen settings.";
  const fmtMB = (b: number) =>
    b >= 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB`
    : b > 0 ? `${(b / 1024).toFixed(0)} KB` : "—";
  const pct = bytesTotal > 0 ? Math.round((bytesReceived / bytesTotal) * 100) : null;

  return (
    <div
      className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-4 backdrop-blur-[1px]"
      style={{ background: "rgba(255,253,247,0.92)" }}
      role="status"
      aria-live="polite"
    >
      {/* Animated rotating ring + paper-folio dots */}
      <div className="relative w-14 h-14">
        <div className="absolute inset-0 rounded-full border-2"
             style={{
               borderColor: "var(--rule)",
               borderTopColor: "var(--accent)",
               animation: "np-spin 0.9s linear infinite",
             }} />
        <div className="absolute inset-2 rounded-full border"
             style={{
               borderColor: "var(--rule-strong)",
               borderTopColor: "transparent",
               borderBottomColor: "var(--accent)",
               animation: "np-spin 1.4s linear infinite reverse",
             }} />
      </div>

      <div className="text-center">
        <div className="font-display text-[20px] leading-none">{headline}</div>
        <div className="font-mono text-[11px] text-[var(--fg-muted)] mt-1.5 max-w-[360px]">
          {sub}
        </div>
      </div>

      {/* Progress bar — shows determinate progress once Content-Length arrives,
         otherwise an indeterminate pulse so the user sees motion. */}
      <div className="w-[280px]">
        <div className="h-1 w-full overflow-hidden"
             style={{ background: "var(--bg-inset)" }}>
          {pct == null ? (
            <div className="h-full w-1/3"
                 style={{
                   background: "var(--accent)",
                   animation: "np-slide 1.2s ease-in-out infinite",
                 }} />
          ) : (
            <div className="h-full transition-all duration-200"
                 style={{ width: `${pct}%`, background: "var(--accent)" }} />
          )}
        </div>
        <div className="mt-1.5 flex items-center justify-between font-mono text-[10px] tabular-nums text-[var(--fg-muted)]">
          <span>{fmtMB(bytesReceived)}{pct != null && bytesTotal > 0 ? ` / ${fmtMB(bytesTotal)}` : ""}</span>
          <span>{pct != null ? `${pct}%` : "working…"}</span>
        </div>
      </div>

      <button
        type="button"
        onClick={onCancel}
        className="np-btn-ghost text-[11px] mt-1"
      >
        Cancel
      </button>

      <style>{`
        @keyframes np-spin { to { transform: rotate(360deg); } }
        @keyframes np-slide {
          0% { transform: translateX(-100%); }
          50% { transform: translateX(120%); }
          100% { transform: translateX(300%); }
        }
      `}</style>
    </div>
  );
}
